import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';
import { getMaxUploadSizeByChannel } from '@chatwoot/utils';
import { DirectUpload } from 'activestorage';
import {
  DEFAULT_MAXIMUM_FILE_UPLOAD_SIZE,
  resolveMaximumFileUploadSize,
} from 'shared/helpers/FileHelper';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

export default {
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
    }),
    installationLimit() {
      return resolveMaximumFileUploadSize(
        this.globalConfig.maximumFileUploadSize
      );
    },
  },

  methods: {
    maxSizeFor(mime) {
      // Use default/installation limit for private notes
      if (this.isOnPrivateNote) {
        return this.installationLimit;
      }

      const channelType = this.inbox?.channel_type;

      if (!channelType || channelType === INBOX_TYPES.WEB) {
        return this.installationLimit;
      }

      const channelLimit = getMaxUploadSizeByChannel({
        channelType,
        medium: this.inbox?.medium, // e.g. 'sms' | 'whatsapp'
        mime, // e.g. 'image/png'
      });

      if (channelLimit === DEFAULT_MAXIMUM_FILE_UPLOAD_SIZE) {
        return this.installationLimit;
      }

      return Math.min(channelLimit, this.installationLimit);
    },
    alertOverLimit(maxSizeMB) {
      useAlert(
        this.$t('CONVERSATION.FILE_SIZE_LIMIT', {
          MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE: maxSizeMB,
        })
      );
    },
    onFileUpload(file) {
      if (this.globalConfig.directUploadsEnabled) {
        this.onDirectFileUpload(file);
      } else {
        this.onIndirectFileUpload(file);
      }
    },

    onDirectFileUpload(file) {
      if (!file) return;

      const mime = file.file?.type || file.type;
      const maxSizeMB = this.maxSizeFor(mime);

      if (!checkFileSizeLimit(file, maxSizeMB)) {
        this.alertOverLimit(maxSizeMB);
        return;
      }

      const directUploadUrl = `/api/v1/accounts/${this.accountId}/conversations/${this.currentChat.id}/direct_uploads`;
      // eslint-disable-next-line no-console
      console.log('[audio-debug] fileUploadMixin:onDirectFileUpload:start', {
        directUploadUrl,
        fileName: file?.name,
        fileType: file?.type,
        fileSize: file?.size,
        innerFileType: file?.file?.type,
        innerFileSize: file?.file?.size,
        isRecordedAudio: file?.isRecordedAudio,
        location: window?.location?.origin,
      });
      const startedAt = Date.now();

      const upload = new DirectUpload(
        file.file,
        directUploadUrl,
        {
          directUploadWillCreateBlobWithXHR: xhr => {
            xhr.setRequestHeader(
              'api_access_token',
              this.currentUser.access_token
            );
          },
        }
      );

      upload.create((error, blob) => {
        const durationMs = Date.now() - startedAt;
        if (error) {
          // eslint-disable-next-line no-console
          console.error('[audio-debug] fileUploadMixin:onDirectFileUpload:error', {
            durationMs,
            error,
            errorString: String(error),
          });
          useAlert(error);
        } else {
          // eslint-disable-next-line no-console
          console.log('[audio-debug] fileUploadMixin:onDirectFileUpload:success', {
            durationMs,
            blobSignedId: blob?.signed_id,
            blobKey: blob?.key,
            blobFilename: blob?.filename,
            blobContentType: blob?.content_type,
            blobByteSize: blob?.byte_size,
            blobServiceUrl: blob?.service_url,
            blobAttributes: blob?.attributes,
          });
          this.attachFile({ file, blob });
        }
      });
    },

    onIndirectFileUpload(file) {
      if (!file) return;

      const mime = file.file?.type || file.type;
      const maxSizeMB = this.maxSizeFor(mime);

      if (!checkFileSizeLimit(file, maxSizeMB)) {
        this.alertOverLimit(maxSizeMB);
        return;
      }

      // eslint-disable-next-line no-console
      console.log('[audio-debug] fileUploadMixin:onIndirectFileUpload', {
        fileName: file?.name,
        fileType: file?.type,
        fileSize: file?.size,
        innerFileType: file?.file?.type,
        innerFileSize: file?.file?.size,
        isRecordedAudio: file?.isRecordedAudio,
      });

      this.attachFile({ file });
    },
  },
};
